import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { Badge, type BadgeTone } from "~/components/ui/badge";
import { Page, PageHeader, Panel, Stat } from "~/components/ui/page";
import { ErrorState, Skeleton } from "~/components/ui/states";
import { api, unwrap } from "~/lib/api";
import { useFormat, useT } from "~/lib/i18n";

export const Route = createFileRoute("/_app/stock/serials/$id")({ component: SerialPage });

const STATUS_TONE: Record<string, BadgeTone> = {
  in_stock: "success",
  shipped: "info",
  quarantine: "warning",
  scrapped: "danger",
  exited: "neutral",
};

/** One physical unit: what it is, where it is, and everything that happened to it. */
function SerialPage() {
  const { id } = Route.useParams();
  const t = useT();
  const format = useFormat();
  const query = useQuery({ queryKey: ["stock", "serial", id], queryFn: () => unwrap(api.stock.serials({ id }).get()) });
  if (query.isPending) return <Skeleton className="h-96 w-full" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const unit = query.data;
  const links: { label: string; to: string; params: { id: string }; text: string }[] = [];
  if (unit.purchaseOrderId && unit.purchaseOrderNumber)
    links.push({
      label: t("stock.serials.receivedWith", { number: unit.purchaseOrderNumber }),
      to: "/purchasing/$id",
      params: { id: unit.purchaseOrderId },
      text: unit.purchaseOrderNumber,
    });
  if (unit.shipmentId && unit.shipmentNumber)
    links.push({
      label: t("stock.serials.shippedWith", { number: unit.shipmentNumber }),
      to: "/shipments/$id",
      params: { id: unit.shipmentId },
      text: unit.shipmentNumber,
    });
  if (unit.orderId && unit.orderNumber)
    links.push({
      label: t("stock.serials.order", { number: unit.orderNumber }),
      to: "/orders/$id",
      params: { id: unit.orderId },
      text: unit.orderNumber,
    });
  if (unit.customerId && unit.customerName)
    links.push({
      label: t("stock.serials.customer"),
      to: "/customers/$id",
      params: { id: unit.customerId },
      text: unit.customerName,
    });
  if (unit.savTicketId && unit.savNumber)
    links.push({
      label: t("stock.serials.sav", { number: unit.savNumber }),
      to: "/sav/$id",
      params: { id: unit.savTicketId },
      text: unit.savNumber,
    });
  return (
    <Page>
      <PageHeader
        back={
          <Link to="/stock" className="mb-2 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg">
            <ChevronLeft className="size-4" aria-hidden />
            {t("stock.title")}
          </Link>
        }
        title={<span className="tabular">{t("stock.serials.unit", { serial: unit.serial })}</span>}
        meta={<Badge tone={STATUS_TONE[unit.status] ?? "neutral"}>{t(`stock.serials.statuses.${unit.status}`)}</Badge>}
      />
      <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <Panel>
          <div className="px-5 py-5 md:px-6">
            <h2 className="mb-3 text-[15px] font-medium">{t("stock.serials.history")}</h2>
            <ol className="flex flex-col divide-y divide-line">
              {unit.events.map((e) => (
                <li key={e.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                  <div className="min-w-0">
                    <p>{t(`stock.serials.events.${e.type}`)}</p>
                    {e.note ? <p className="truncate text-xs text-fg-3">{e.note}</p> : null}
                  </div>
                  <span className="shrink-0 text-xs text-fg-3 tabular">{format.date(e.createdAt, "datetime")}</span>
                </li>
              ))}
            </ol>
          </div>
        </Panel>
        <aside className="flex flex-col gap-5">
          <Panel>
            <div className="px-5 py-5">
              <dl className="grid gap-4">
                <Stat
                  label={t("stock.serials.product")}
                  value={
                    <Link to="/products/$id" params={{ id: unit.productId }} className="hover:underline">
                      {unit.title}
                    </Link>
                  }
                />
                <Stat label={t("products.fields.sku")} value={<span className="tabular">{unit.sku}</span>} />
                {unit.receivedAt ? (
                  <Stat label={t("stock.serials.events.received")} value={format.date(unit.receivedAt)} />
                ) : null}
              </dl>
            </div>
          </Panel>
          {links.length ? (
            <Panel>
              <div className="px-5 py-5">
                <h2 className="mb-2 text-[15px] font-medium">{t("stock.serials.links")}</h2>
                <ul className="flex flex-col gap-1.5 text-sm">
                  {links.map((l) => (
                    <li key={l.label}>
                      <Link to={l.to} params={l.params} className="hover:underline">
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </Panel>
          ) : null}
        </aside>
      </div>
    </Page>
  );
}
